import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { ResponsiveSunburst } from '@nivo/sunburst';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import ChartCard, { ChartCardIconButton, ChartCardButton } from '../shared/ChartCard.js';
import { buildExportSvg, downloadSvg } from '../shared/svgExport.js';
import {
    BREAKDOWN_GRADIENT,
    colorsAlongGradient,
    OTHER_COLOR,
    DARK_TEXT,
    MUTED_TEXT,
    FONT_STACK,
    formatValue,
    formatPercent,
    subcategoryColor,
    polarToCartesian,
    distributeNatural,
    distributeEqually,
} from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

const TOP_MARGIN = 40;
const LEGEND_ROW_HEIGHT = 26;
const LEGEND_ITEM_GAP = 28;
const BOTTOM_PADDING = 56;
const LABEL_COL_GAP = 10;

const DEFAULT_SETTINGS = {
    fontSize: 17, // taille de police des labels connecteurs (légende = +0.5pt)
    connectorLength: 0, // longueur du 1er segment (diagonal court) des connecteurs
    connectorColor: '#c7cad3', // couleur des traits connecteurs
    chartSize: 175, // rayon du donut
    ringThickness: 2, // épaisseur des séparateurs blancs entre segments
    labelSpacing: 26, // espacement vertical minimal entre labels connecteurs
    equalSpacing: false, // répartition équitable au lieu d'un espacement au plus près
    showPercent: true,
    roundPercent: true, // arrondit les % à l'unité (sauf en dessous de 1%, gardé en décimale)
    showValue: false,
    rotation: 0, // rotation du donut (degrés) — pour éloigner un sous-usage d'une zone gênante (ex. tout en bas)
    centerFontSize: 36, // taille de police du total central (px)
    legendGap: 40, // écart entre le bas du donut et la première ligne de légende (px)
    showConnectors: true, // afficher les labels de sous-usages reliés par des traits
    showLegend: true, // afficher la légende des catégories sous le graphique
    categoryGap: 0, // espace blanc (degrés) entre catégories principales, pas entre leurs sous-usages
    cornerRadius: 3, // arrondi des coins des arcs
};

// Compose "nom · valeur m³ (pourcentage %)" en respectant les préférences d'affichage.
const buildLabelParts = (value, percentage, unit, showValue, showPercent, roundPercent) => {
    const valuePart = showValue ? ` · ${formatValue(value)} ${unit}` : '';
    const percentPart = showPercent ? ` (${formatPercent(percentage, roundPercent)}%)` : '';
    return `${valuePart}${percentPart}`;
};

// Mesure réelle (canvas) plutôt qu'une estimation par nombre de caractères : les
// marges et le retour à la ligne de la légende dépendent d'une largeur exacte,
// sans quoi un texte plus large que prévu déborde du cadre du SVG exporté.
let measureCanvasCtx = null;
const estimateTextWidth = (text, fontSize = 12, weight = 500) => {
    if (!measureCanvasCtx) measureCanvasCtx = document.createElement('canvas').getContext('2d');
    measureCanvasCtx.font = `${weight >= 600 ? 700 : 500} ${fontSize}px ${FONT_STACK}`;
    return measureCanvasCtx.measureText(text).width + 6;
};

// Nivo ne propose pas d'espacement angulaire ("padAngle") pour le Sunburst : on le
// simule en resserrant chaque catégorie (angles bruts, avant rotation) de gapRad/2 de
// chaque côté, puis en ré-échelonnant ses sous-usages proportionnellement dans ce
// nouvel intervalle. Le blanc n'est donc introduit qu'aux frontières entre deux
// catégories — deux sous-usages voisins d'une MÊME catégorie restent collés, mais deux
// sous-usages voisins de catégories DIFFÉRENTES héritent bien de l'écart (ils sont
// chacun repoussés vers l'intérieur de leur propre catégorie resserrée). Retourne une
// Map id -> {startAngle, endAngle}.
const applyCategoryGap = (nodes, gapRad) => {
    const catRanges = new Map();
    nodes.forEach((node) => {
        if (node.depth === 1) catRanges.set(node.id, { start: node.arc.startAngle, end: node.arc.endAngle });
    });

    const result = new Map();
    nodes.forEach((node) => {
        // Nivo ne peuple pas réellement `node.parent` à l'exécution malgré le typage qui
        // le suggère : node.path (self→parent→racine) donne l'id du parent de façon
        // fiable pour un nœud depth 2 (un nœud depth 1 utilise directement son propre id).
        const catId = node.depth === 1 ? node.id : node.path[1];
        const cat = catRanges.get(catId);
        const span = cat ? cat.end - cat.start : 0;

        if (!cat || gapRad <= 0 || span <= gapRad) {
            result.set(node.id, { startAngle: node.arc.startAngle, endAngle: node.arc.endAngle });
            return;
        }

        const newStart = cat.start + gapRad / 2;
        const newEnd = cat.end - gapRad / 2;
        if (node.depth === 1) {
            result.set(node.id, { startAngle: newStart, endAngle: newEnd });
        } else {
            const relStart = (node.arc.startAngle - cat.start) / span;
            const relEnd = (node.arc.endAngle - cat.start) / span;
            result.set(node.id, {
                startAngle: newStart + relStart * (newEnd - newStart),
                endAngle: newStart + relEnd * (newEnd - newStart),
            });
        }
    });
    return result;
};

// Layer custom qui remplace le layer natif 'arcs' de Nivo : seule façon d'appliquer une
// rotation au donut tout en gardant les connecteurs synchronisés (Nivo Sunburst n'a pas
// de prop de rotation ; `arcGenerator` fourni par le layer permet de redessiner les arcs
// nous-mêmes à partir d'angles décalés de `rotationRad`). Gère aussi son propre survol
// (tooltip) puisqu'on sort du système d'interactivité intégré à Nivo.
const ArcsLayer = ({ nodes, centerX, centerY, arcGenerator, rotationRad, categoryGapRad, borderWidth, onHover, onLeave }) => {
    const gapped = applyCategoryGap(nodes, categoryGapRad);
    return (
        <g transform={`translate(${centerX}, ${centerY})`}>
            {nodes.map((node) => {
                const g = gapped.get(node.id) || node.arc;
                const rotatedArc = {
                    ...node.arc,
                    startAngle: g.startAngle + rotationRad,
                    endAngle: g.endAngle + rotationRad,
                };
                return (
                    <path
                        key={node.id}
                        d={arcGenerator(rotatedArc)}
                        fill={node.color}
                        stroke="#ffffff"
                        strokeWidth={borderWidth}
                        onMouseMove={(e) => onHover(node, e)}
                        onMouseEnter={(e) => onHover(node, e)}
                        onMouseLeave={onLeave}
                    />
                );
            })}
        </g>
    );
};

// Layer custom : uniquement des labels externes pour les sous-usages (aucune étiquette
// sur le graphique lui-même), reliés par un connecteur en coude (diagonale courte puis
// horizontale), répartis verticalement pour ne jamais se chevaucher. `rotationRad` et
// `categoryGapRad` doivent correspondre exactement à ceux appliqués aux arcs par
// ArcsLayer, pour que chaque connecteur reste ancré au bon endroit sur le bon segment.
const ConnectorsLayer = ({ nodes, centerX, centerY, radius, settings, unit, rotationRad, categoryGapRad }) => {
    const right = [];
    const left = [];

    if (settings.showConnectors) {
        const gapped = applyCategoryGap(nodes, categoryGapRad);
        nodes
            .filter((node) => node.depth === 2)
            .forEach((node) => {
                const { outerRadius } = node.arc;
                const { startAngle, endAngle } = gapped.get(node.id) || node.arc;
                const midAngle = (startAngle + endAngle) / 2 + rotationRad;
                const anchor = polarToCartesian(centerX, centerY, outerRadius, midAngle);
                const side = Math.sin(midAngle) >= 0 ? 1 : -1;
                (side === 1 ? right : left).push({
                    id: node.id,
                    anchor,
                    side,
                    y: anchor.y,
                    label: node.data.label,
                    dotColor: node.data.categoryColor,
                    value: node.value,
                    percentage: node.percentage,
                });
            });
    }

    const distribute = settings.equalSpacing ? distributeEqually : distributeNatural;
    const items = [...distribute(right, settings.labelSpacing), ...distribute(left, settings.labelSpacing)];
    const labelColX = radius + settings.connectorLength + LABEL_COL_GAP + 40;

    return (
        <g>
            {items.map((item) => {
                const elbow = { x: item.anchor.x + item.side * settings.connectorLength, y: item.y };
                const labelX = centerX + item.side * labelColX;
                const textX = labelX + item.side * 12; // laisse la place à la pastille
                return (
                    <g key={item.id}>
                        <polyline
                            points={`${item.anchor.x},${item.anchor.y} ${elbow.x},${elbow.y} ${labelX - item.side * 4},${item.y}`}
                            fill="none"
                            stroke={settings.connectorColor}
                            strokeWidth={1}
                        />
                        <circle cx={labelX} cy={item.y} r={4} fill={item.dotColor} />
                        <text
                            x={textX}
                            y={item.y}
                            textAnchor={item.side === 1 ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{ fontSize: settings.fontSize, fontFamily: FONT_STACK }}
                        >
                            <tspan fontWeight={600} fill={DARK_TEXT}>
                                {item.label}
                            </tspan>
                            <tspan fill={MUTED_TEXT}>
                                {buildLabelParts(item.value, item.percentage, unit, settings.showValue, settings.showPercent, settings.roundPercent)}
                            </tspan>
                        </text>
                    </g>
                );
            })}
        </g>
    );
};

// Layer custom : légende sobre sous le graphique, catégories principales uniquement,
// en grille qui se répartit sur plusieurs lignes si nécessaire.
const LegendLayer = ({ categories, top, width, centerX, settings }) => {
    if (!settings.showLegend) return null;
    return (
        <g>
            <line x1={centerX - width / 2} y1={top - 12} x2={centerX + width / 2} y2={top - 12} stroke="#e8eaf3" strokeWidth={1} />
            {categories.map((cat) => (
                <g key={cat.id} transform={`translate(${cat.x}, ${cat.y})`}>
                    <circle cx={6} cy={0} r={6} fill={cat.color} />
                    <text x={18} y={0} dominantBaseline="central" style={{ fontSize: settings.fontSize + 0.5, fontFamily: FONT_STACK }}>
                        <tspan fontWeight={700} fill={DARK_TEXT}>
                            {cat.label}
                        </tspan>
                        <tspan fill={MUTED_TEXT}>{cat.labelSuffix}</tspan>
                    </text>
                </g>
            ))}
        </g>
    );
};

// Layer custom : le total au centre du donut. Rendu en texte SVG plutôt qu'en overlay
// HTML positionné par-dessus le graphique — un overlay HTML n'existe que dans la page,
// il disparaît donc du fichier lors de l'export SVG (`buildExportSvg` ne clone que le
// <svg> lui-même). En SVG, il est capturé avec le reste.
const CENTER_TEXT = '#205673'; // total central (et libellé TOTAL), bleu pétrole de la palette
const EXPORT_PADDING_RIGHT = 12; // marge ajoutée à droite du SVG exporté (px)

const CenterLayer = ({ centerX, centerY, total, unit, settings }) => {
    const valueSize = settings.centerFontSize;
    const eyebrowSize = Math.max(10, settings.centerFontSize * 0.28);
    return (
        <g style={{ pointerEvents: 'none' }}>
            <text
                x={centerX}
                y={centerY - valueSize * 0.32}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: valueSize, fontWeight: 700, fontFamily: FONT_STACK, fill: CENTER_TEXT }}
            >
                {formatValue(total)} {unit}
            </text>
            <text
                x={centerX}
                y={centerY + valueSize * 0.42}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: eyebrowSize, fontWeight: 600, fontFamily: FONT_STACK, fill: CENTER_TEXT, letterSpacing: '0.03em' }}
            >
                TOTAL
            </text>
        </g>
    );
};

// Regroupées par thème plutôt qu'en une seule liste plate : plus facile à parcourir,
// et ça se prête naturellement à une grille à deux colonnes dans le panneau.
// `terms` : libellés des deux anneaux (cf. DEFAULT_TERMS), pour réutiliser la carte
// en « section et pièce ».
const DEFAULT_TERMS = { inner: 'catégories', outer: 'sous-usages' };

const buildSettingsSections = (terms) => [
    {
        id: 'donut',
        title: 'Donut',
        fields: [
            { key: 'chartSize', label: 'Taille du donut', type: 'range', min: 110, max: 220, step: 5, unit: 'px' },
            { key: 'cornerRadius', label: 'Arrondi des coins', type: 'range', min: 0, max: 24, step: 1, unit: 'px' },
            { key: 'ringThickness', label: 'Épaisseur des séparateurs', type: 'range', min: 0, max: 4, step: 0.5, unit: 'px' },
            { key: 'categoryGap', label: `Espace entre ${terms.inner}`, type: 'range', min: 0, max: 20, step: 1, unit: '°' },
            { key: 'rotation', label: 'Rotation du graphique', type: 'range', min: 0, max: 355, step: 5, unit: '°' },
        ],
    },
    {
        id: 'connectors',
        title: 'Connecteurs',
        fields: [
            { key: 'connectorLength', label: 'Longueur des connecteurs', type: 'range', min: 0, max: 50, step: 2, unit: 'px' },
            { key: 'connectorColor', label: 'Couleur des connecteurs', type: 'color' },
            { key: 'labelSpacing', label: 'Espacement des labels', type: 'range', min: 12, max: 32, step: 1, unit: 'px' },
            { key: 'equalSpacing', label: 'Répartition équitable des labels', type: 'checkbox' },
        ],
    },
    {
        id: 'typography',
        title: 'Typographie',
        fields: [
            { key: 'fontSize', label: 'Taille de police', type: 'range', min: 10, max: 24, step: 1, unit: 'px' },
            { key: 'centerFontSize', label: 'Taille du total central', type: 'range', min: 16, max: 40, step: 1, unit: 'px' },
        ],
    },
    {
        id: 'content',
        title: 'Contenu affiché',
        fields: [
            { key: 'showValue', label: 'Afficher les m³', type: 'checkbox' },
            { key: 'showPercent', label: 'Afficher les pourcentages', type: 'checkbox' },
            { key: 'roundPercent', label: 'Arrondir les pourcentages', type: 'checkbox' },
            { key: 'showConnectors', label: `Afficher les labels de ${terms.outer}`, type: 'checkbox' },
            { key: 'showLegend', label: `Afficher la légende des ${terms.inner}`, type: 'checkbox' },
            { key: 'legendGap', label: 'Écart avec la légende', type: 'range', min: 0, max: 160, step: 2, unit: 'px' },
        ],
    },
];

const SettingsField = ({ field, settings, onChange }) => (
    <div className="sunburst-settings-row" key={field.key}>
        <label htmlFor={`sunburst-setting-${field.key}`}>{field.label}</label>
        {field.type === 'range' ? (
            <div className="sunburst-settings-range">
                <input
                    id={`sunburst-setting-${field.key}`}
                    type="range"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={settings[field.key]}
                    onChange={(e) => onChange(field.key, Number(e.target.value))}
                />
                <span className="sunburst-settings-value">
                    {settings[field.key]}
                    {field.unit}
                </span>
            </div>
        ) : field.type === 'color' ? (
            <input
                id={`sunburst-setting-${field.key}`}
                type="color"
                className="sunburst-settings-color"
                value={settings[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
            />
        ) : (
            <label className="sunburst-settings-toggle">
                <input
                    id={`sunburst-setting-${field.key}`}
                    type="checkbox"
                    checked={settings[field.key]}
                    onChange={(e) => onChange(field.key, e.target.checked)}
                />
                <span className="sunburst-settings-toggle-slider" />
            </label>
        )}
    </div>
);

const SettingsPanel = ({ settings, onChange, onClose, terms }) => (
    <div className="sunburst-settings-panel">
        <div className="sunburst-settings-panel-header">
            <span>Réglages d'affichage</span>
            <button type="button" className="sunburst-settings-close" onClick={onClose} aria-label="Fermer">
                <FaTimes />
            </button>
        </div>
        <div className="sunburst-settings-grid">
            {buildSettingsSections(terms).map((section) => (
                <div className="sunburst-settings-section" key={section.id}>
                    <div className="sunburst-settings-section-title">{section.title}</div>
                    {section.fields.map((field) => (
                        <SettingsField key={field.key} field={field} settings={settings} onChange={onChange} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   V2 — pensée pour être copiée/exportée telle quelle dans un rapport client.
   Aucune étiquette sur le donut : les sous-usages sont reliés par un connecteur en
   coude à un label externe (pastille = couleur pleine de la catégorie parente, arc =
   déclinaison plus claire de cette même teinte). Les catégories elles-mêmes ne portent
   aucun texte sur le graphique — elles sont listées dans la légende en dessous.
   Tout (donut, connecteurs, labels, légende) est dessiné dans le même <svg>, pour un
   export en un seul fichier prêt à coller dans un document. Un panneau de réglages
   permet d'ajuster typographie, taille et espacements avant l'export.

   @param {{id:string, label:string, value:number}[]} categoryTotals
   @param {{id:string, categoryId:string, label:string, value:number}[]} subcategoryTotals
   @param {string} unit
   @param {string} [title]
   @param {string} [fileName] - nom du SVG téléchargé
   @param {{inner:string, outer:string}} [terms] - libellés des anneaux dans les réglages
───────────────────────────────────────────── */
const UsageTaxonomySunburstReportCard = ({
    categoryTotals,
    subcategoryTotals,
    unit,
    headerActions,
    title = "Répartition par catégorie d'usage",
    fileName = 'repartition-par-categorie-usage-rapport.svg',
    terms = DEFAULT_TERMS,
}) => {
    const containerRef = useRef(null);
    const [downloadState, setDownloadState] = useState('idle');
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [panelOpen, setPanelOpen] = useState(false);
    const [hoverInfo, setHoverInfo] = useState(null); // { node, x, y } — tooltip maison (cf. ArcsLayer)
    const updateSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
    const rotationRad = (settings.rotation * Math.PI) / 180;
    const categoryGapRad = (settings.categoryGap * Math.PI) / 180;

    // Stables (deps vides) : ArcsLayer est mémoïsé via useCallback plus bas, et ne doit
    // se redémonter QUE si son propre rendu doit changer — pas à chaque survol. Si ces
    // handlers changeaient de référence à chaque hover, ce serait le cas (cf. plus bas),
    // ce qui interrompt la réception du mouseleave par le navigateur (tooltip figé).
    const handleArcHover = useCallback((node, event) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setHoverInfo({ node, x: event.clientX - rect.left, y: event.clientY - rect.top });
    }, []);
    const handleArcLeave = useCallback(() => setHoverInfo(null), []);

    // Fira Sans se charge de façon asynchrone (Google Fonts) : si les marges basées
    // sur `estimateTextWidth` sont calculées avant qu'elle soit prête, la mesure
    // canvas tombe sur une police de repli (souvent plus étroite), ce qui sous-estime
    // la largeur réelle et fait déborder les labels/la légende. On force un recalcul
    // dès que la police est effectivement chargée.
    const [fontsReadyTick, setFontsReadyTick] = useState(0);
    useEffect(() => {
        if (typeof document !== 'undefined' && document.fonts?.ready) {
            document.fonts.ready.then(() => setFontsReadyTick((n) => n + 1));
        }
    }, []);

    const total = Math.round(categoryTotals.reduce((sum, c) => sum + c.value, 0));

    const categoryColorById = useMemo(() => {
        const colors = colorsAlongGradient(categoryTotals, BREAKDOWN_GRADIENT, (cat) => cat.id === 'uncategorized');
        return Object.fromEntries(categoryTotals.map((cat, i) => [cat.id, colors[i]]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryTotals]);

    const tree = useMemo(() => {
        const countByCategory = {};
        subcategoryTotals.forEach((s) => {
            countByCategory[s.categoryId] = (countByCategory[s.categoryId] || 0) + 1;
        });
        const indexByCategory = {};

        return {
            id: 'root',
            children: categoryTotals.map((cat) => {
                const baseColor = categoryColorById[cat.id] || OTHER_COLOR;
                const children = subcategoryTotals
                    .filter((s) => s.categoryId === cat.id)
                    .map((sub) => {
                        const count = countByCategory[cat.id] || 1;
                        const i = indexByCategory[cat.id] || 0;
                        indexByCategory[cat.id] = i + 1;
                        return {
                            id: sub.id,
                            label: sub.label,
                            value: sub.value,
                            fillColor: subcategoryColor(baseColor, i, count),
                            categoryColor: baseColor,
                            categoryLabel: cat.label,
                        };
                    });
                return { id: cat.id, label: cat.label, fillColor: baseColor, categoryColor: baseColor, children };
            }),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryTotals, subcategoryTotals, categoryColorById]);

    // Marge latérale : calculée à partir du plus long label de sous-usage réellement
    // affiché en connecteur.
    const sideMargin = useMemo(() => {
        const maxLabelWidth = subcategoryTotals.reduce((max, sub) => {
            const pct = total > 0 ? (sub.value / total) * 100 : 0;
            const text = `${sub.label}${buildLabelParts(sub.value, pct, unit, settings.showValue, settings.showPercent, settings.roundPercent)}`;
            return Math.max(max, estimateTextWidth(text, settings.fontSize, 600));
        }, 0);
        return Math.max(140, settings.connectorLength + LABEL_COL_GAP + 40 + maxLabelWidth + 18);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        subcategoryTotals,
        total,
        unit,
        settings.showValue,
        settings.showPercent,
        settings.roundPercent,
        settings.fontSize,
        settings.connectorLength,
        fontsReadyTick,
    ]);

    // Légende : grille à colonnes de largeur fixe (= le plus large libellé mesuré).
    // Contrairement à un flux empaqueté dynamiquement, une largeur de colonne fixe
    // ne peut jamais déborder, quelle que soit l'estimation de texte.
    const chartWidth = settings.chartSize * 2 + sideMargin * 2;
    const legend = useMemo(() => {
        const items = categoryTotals.map((cat) => {
            const pct = total > 0 ? (cat.value / total) * 100 : 0;
            const labelSuffix = buildLabelParts(cat.value, pct, unit, settings.showValue, settings.showPercent, settings.roundPercent);
            const text = `${cat.label}${labelSuffix}`;
            return {
                id: cat.id,
                label: cat.label,
                labelSuffix,
                value: cat.value,
                percentage: pct,
                color: categoryColorById[cat.id] || OTHER_COLOR,
                width: 18 + estimateTextWidth(text, settings.fontSize + 0.5, 700),
            };
        });
        const maxItemWidth = items.reduce((max, it) => Math.max(max, it.width), 0);
        const columnWidth = maxItemWidth + LEGEND_ITEM_GAP;
        const availableWidth = chartWidth - 48;
        let columns = Math.min(items.length, 4);
        while (columns > 1 && columns * columnWidth > availableWidth) columns -= 1;

        const rowCount = Math.ceil(items.length / columns);
        const positioned = items.map((item, i) => ({
            ...item,
            col: i % columns,
            row: Math.floor(i / columns),
        }));
        const rowCounts = Array.from({ length: rowCount }, (_, r) => positioned.filter((it) => it.row === r).length);
        const widestRow = Math.max(...rowCounts) * columnWidth;
        return { items: positioned, rowCount, columnWidth, rowCounts, widestRow };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        categoryTotals,
        categoryColorById,
        chartWidth,
        total,
        unit,
        settings.showValue,
        settings.showPercent,
        settings.roundPercent,
        settings.fontSize,
        fontsReadyTick,
    ]);

    // Un sous-usage minuscule peut atterrir tout en bas du donut (proche de 180°), et la
    // répartition anti-chevauchement peut encore le pousser plus bas pour l'espacer de
    // ses voisins. Mesurer cette position réelle après coup (via un effet remontant la
    // valeur depuis ConnectorsLayer) créait une boucle de rétroaction avec le
    // redimensionnement du conteneur (mesure → agrandit → le conteneur redimensionné
    // déclenche une nouvelle mesure → ...). Sans légende, on réserve donc une marge de
    // sécurité fixe, proportionnelle à l'espacement et à la taille de police. Avec
    // légende, son écart au donut est un réglage (legendGap) : à l'utilisateur de
    // l'agrandir si un label du bas vient la chevaucher.
    const contentBottom = settings.showLegend
        ? settings.chartSize * 2 + settings.legendGap
        : settings.chartSize * 2 + settings.labelSpacing * 3 + settings.fontSize;
    const legendTop = TOP_MARGIN + contentBottom;
    const legendHeight = settings.showLegend ? legend.rowCount * LEGEND_ROW_HEIGHT : 0;
    const chartHeight = legendTop + legendHeight + BOTTOM_PADDING;

    // Nivo rend chaque layer (natif ou custom) à l'intérieur d'un <g> déjà translaté
    // de (margin.left, margin.top) — les coordonnées passées à ConnectorsLayer/
    // LegendLayer sont donc LOCALES à ce repère, pas absolues au canevas SVG.
    const localCenterX = settings.chartSize;
    const localLegendTop = contentBottom;

    const legendPositioned = useMemo(
        () =>
            legend.items.map((item) => {
                const rowWidth = legend.rowCounts[item.row] * legend.columnWidth;
                const offsetX = localCenterX - rowWidth / 2;
                return { ...item, x: offsetX + item.col * legend.columnWidth, y: localLegendTop + item.row * LEGEND_ROW_HEIGHT };
            }),
        [legend, localCenterX, localLegendTop]
    );

    const handleDownloadSvg = () => {
        const svgEl = containerRef.current?.querySelector('svg');
        if (!svgEl) return;
        downloadSvg(buildExportSvg(svgEl, { paddingRight: EXPORT_PADDING_RIGHT }), fileName);
        setDownloadState('downloaded');
        setTimeout(() => setDownloadState('idle'), 2000);
    };

    // Chaque layer passé à Nivo DOIT garder une référence stable d'un rendu à l'autre :
    // sinon (fonction inline recréée à chaque render) React démonte/remonte tous les
    // <path>/<text> du graphique à chaque survol — et un <path> démonté en plein hover
    // ne délivre plus son mouseleave, d'où un tooltip qui restait figé à l'écran.
    const renderArcsLayer = useCallback(
        (layerProps) => (
            <ArcsLayer
                {...layerProps}
                rotationRad={rotationRad}
                categoryGapRad={categoryGapRad}
                borderWidth={settings.ringThickness}
                onHover={handleArcHover}
                onLeave={handleArcLeave}
            />
        ),
        [rotationRad, categoryGapRad, settings.ringThickness, handleArcHover, handleArcLeave]
    );

    const renderConnectorsLayer = useCallback(
        (layerProps) => (
            <ConnectorsLayer {...layerProps} settings={settings} unit={unit} rotationRad={rotationRad} categoryGapRad={categoryGapRad} />
        ),
        [settings, unit, rotationRad, categoryGapRad]
    );

    const renderLegendLayer = useCallback(
        () => (
            <LegendLayer
                categories={legendPositioned}
                top={localLegendTop}
                width={legend.widestRow}
                centerX={localCenterX}
                settings={settings}
            />
        ),
        [legendPositioned, localLegendTop, legend.widestRow, localCenterX, settings]
    );

    const renderCenterLayer = useCallback(
        ({ centerX, centerY }) => <CenterLayer centerX={centerX} centerY={centerY} total={total} unit={unit} settings={settings} />,
        [total, unit, settings]
    );

    return (
        <ChartCard
            title={title}
            bodyClassName="sunburst-report-body"
            actions={
                <>
                    {headerActions}
                    <ChartCardIconButton
                        onClick={() => setPanelOpen((v) => !v)}
                        title="Réglages d'affichage"
                        aria-label="Réglages d'affichage"
                    >
                        <FaSlidersH />
                    </ChartCardIconButton>
                    <ChartCardButton onClick={handleDownloadSvg} title="Télécharger le graphique en SVG">
                        {downloadState === 'downloaded' ? 'SVG téléchargé' : 'Télécharger en SVG'}
                    </ChartCardButton>
                </>
            }
            panel={panelOpen && <SettingsPanel settings={settings} onChange={updateSetting} onClose={() => setPanelOpen(false)} terms={terms} />}
        >
                <div className="sunburst-chart-area" ref={containerRef} style={{ width: chartWidth, height: chartHeight }}>
                    <ResponsiveSunburst
                        data={tree}
                        id="id"
                        value="value"
                        valueFormat={(v) => `${formatValue(v)} ${unit}`}
                        margin={{
                            top: TOP_MARGIN,
                            right: sideMargin,
                            bottom: chartHeight - TOP_MARGIN - settings.chartSize * 2,
                            left: sideMargin,
                        }}
                        cornerRadius={settings.cornerRadius}
                        colors={(node) => node.data.fillColor}
                        inheritColorFromParent={false}
                        enableArcLabels={false}
                        animate={true}
                        motionConfig="gentle"
                        theme={{ text: { fontFamily: FONT_STACK } }}
                        layers={[renderArcsLayer, renderConnectorsLayer, renderLegendLayer, renderCenterLayer]}
                    />

                    {hoverInfo && (
                        <div
                            className="sunburst-tooltip"
                            style={{ position: 'absolute', left: hoverInfo.x + 14, top: hoverInfo.y + 14, pointerEvents: 'none' }}
                        >
                            <div className="sunburst-tooltip-head">
                                <span
                                    className="sunburst-tooltip-swatch"
                                    style={{
                                        background:
                                            hoverInfo.node.depth === 2 ? hoverInfo.node.data.categoryColor : hoverInfo.node.data.fillColor,
                                    }}
                                />
                                <div>
                                    {hoverInfo.node.depth === 2 && (
                                        <div className="sunburst-tooltip-breadcrumb">{hoverInfo.node.data.categoryLabel}</div>
                                    )}
                                    <div className="sunburst-tooltip-label">{hoverInfo.node.data.label}</div>
                                </div>
                            </div>
                            <div className="sunburst-tooltip-value">
                                {formatValue(hoverInfo.node.value)} {unit}
                            </div>
                            <div className="sunburst-tooltip-percent">
                                {formatPercent(hoverInfo.node.percentage, settings.roundPercent)}% du total
                            </div>
                        </div>
                    )}

                </div>
        </ChartCard>
    );
};

export default UsageTaxonomySunburstReportCard;
